import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Calendar, MapPin, ChevronDown } from 'lucide-react';
import { stateAbbreviations } from '../../constants/locations';
import './Modals.css';
import './CityResolutionModal.css';

const CityResolutionModal = ({ 
  isOpen, 
  onClose, 
  resolutionData, 
  selectedDateRange,
  availableYears,
  onLocationSelect,
  onYearChange 
}) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedYear, setSelectedYear] = useState(selectedDateRange);
  const [openYearDropdown, setOpenYearDropdown] = useState(null);
  const yearButtonRefs = useRef({});
  const yearMenuRefs = useRef({});

  useEffect(() => {
    if (selectedOption?.availableYears?.length > 0) {
      const mostRecentYear = Math.max(...selectedOption.availableYears);
      setSelectedYear(mostRecentYear.toString());
    }
  }, [selectedOption]);

  useEffect(() => {
    if (resolutionData?.cityOptions?.length > 0) {
      const currentOption = resolutionData.cityOptions.find(
        option => option.city === resolutionData.resolvedCity
      ) || resolutionData.cityOptions[0];
      
      setSelectedOption(currentOption);
    }
  }, [resolutionData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openYearDropdown !== null) {
        const buttonRef = yearButtonRefs.current[openYearDropdown];
        const menuRef = yearMenuRefs.current[openYearDropdown];
        
        if (buttonRef && 
            !buttonRef.contains(event.target) &&
            menuRef &&
            !menuRef.contains(event.target)) {
          setOpenYearDropdown(null);
        }
      }
    };

    if (isOpen && openYearDropdown !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, openYearDropdown]);

  const handleConfirm = useCallback(() => {
    if (selectedOption) {
      const locationName = `${selectedOption.city}, ${resolutionData.state}`;
      
      onClose();
      
      onLocationSelect({
        name: locationName,
        type: 'city',
        info: {
          name: locationName,
          type: 'city',
          state: resolutionData.state,
          yearRange: selectedOption.yearRange,
          isResolved: true,
          skipResolutionCheck: true,
          originalSearch: `${resolutionData.originalCity}, ${resolutionData.state}`,
        }
      });
  
      if (selectedYear !== selectedDateRange) {
        onYearChange(selectedYear);
      }
    }
  }, [selectedOption, resolutionData, selectedYear, selectedDateRange, onClose, onLocationSelect, onYearChange]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (openYearDropdown !== null) {
          setOpenYearDropdown(null);
        } else {
          onClose();
          setTimeout(() => document.activeElement?.blur(), 0);
        }
      } else if (e.key === 'Enter') {
        if (selectedOption && openYearDropdown === null) {
          handleConfirm();
        }
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        if (openYearDropdown === null) {
          e.preventDefault();
          
          const sortedOptions = [...resolutionData.cityOptions].sort((a, b) => b.lastYear - a.lastYear);
          const currentIndex = sortedOptions.findIndex(opt => opt === selectedOption);
          
          let newIndex;
          if (e.key === 'ArrowUp') {
            newIndex = currentIndex > 0 ? currentIndex - 1 : sortedOptions.length - 1;
          } else {
            newIndex = currentIndex < sortedOptions.length - 1 ? currentIndex + 1 : 0;
          }
          
          setSelectedOption(sortedOptions[newIndex]);
        }
      }
    };
  
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
  
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, selectedOption, handleConfirm, isOpen, resolutionData, openYearDropdown]);

  if (!isOpen || !resolutionData) return null;

  const getMatchTypeDescription = (matchType) => {
    switch (matchType) {
      case 'exact':
        return 'Exact location match';
      case 'jurisdiction':
        return 'Jurisdiction within regional department';
      case 'combined':
        return 'Combined with other locations';
      default:
        return '';
    }
  };

  const getMatchTypeIcon = (matchType) => {
    switch (matchType) {
      case 'exact':
        return '✓';
      case 'jurisdiction':
      case 'combined':
        return '🔗';
      default:
        return '';
    }
  };

  const getStateAbbreviation = (stateName) => {
    return stateAbbreviations[stateName] || stateName;
  };

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="city-resolution-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Multiple Location Matches Found</h2>
          <button className="close-button" onClick={onClose}>
            <X size={24} />
          </button>
        </div>
        
        <div className="modal-content">
          <div className="search-info">
            <MapPin size={16} />
            <span>Selected Location: <strong>{resolutionData.originalCity}, {getStateAbbreviation(resolutionData.state)}</strong></span>
          </div>

          <div className="city-options">
            {resolutionData.cityOptions
              .sort((a, b) => b.lastYear - a.lastYear) 
              .map((option, index) => {
                const optionKey = `${option.city}-${index}`;
                const isOpen = openYearDropdown === optionKey && selectedOption === option;
                const availableYearsForOption = option.availableYears
                  .sort((a, b) => b - a)
                  .map(year => ({
                    value: year.toString(),
                    label: year.toString()
                  }));
                
                return (
                  <div 
                    key={index}
                    className={`city-option ${selectedOption === option ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedOption(option);
                      setOpenYearDropdown(null);
                    }}
                  >
                    <div className="option-header">
                      <div className="option-name">
                        <span className="match-icon">{getMatchTypeIcon(option.matchType)}</span>
                        <strong>{option.city}, {getStateAbbreviation(resolutionData.state)}</strong>
                      </div>
                      <div className="year-section">
                        <div className="year-range">
                          <Calendar size={16} />
                          {option.yearRange.includes('-') && option.yearRange.split('-')[0] === option.yearRange.split('-')[1]
                            ? option.yearRange.split('-')[0]
                            : option.yearRange}
                        </div>
                        {selectedOption === option && (
                          <div className="year-selector">
                            <div className={`year-dropdown ${isOpen ? 'open' : ''}`}>
                              <button 
                                className="year-dropdown-button"
                                ref={el => {
                                  if (el) {
                                    yearButtonRefs.current[optionKey] = el;
                                  }
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenYearDropdown(isOpen ? null : optionKey);
                                }}
                              >
                                {availableYearsForOption.find(y => y.value === selectedYear)?.label || availableYearsForOption[0]?.label}
                                <ChevronDown size={16} />
                              </button>
                              {isOpen && yearButtonRefs.current[optionKey] && createPortal(
                                <div 
                                  className="year-dropdown-menu" 
                                  style={{
                                    position: 'fixed',
                                    top: `${yearButtonRefs.current[optionKey].getBoundingClientRect().bottom + 2}px`,
                                    left: `${yearButtonRefs.current[optionKey].getBoundingClientRect().left}px`,
                                    minWidth: `${yearButtonRefs.current[optionKey].getBoundingClientRect().width}px`,
                                    width: 'max-content',
                                    maxWidth: 'max-content',
                                    maxHeight: '335px',
                                    zIndex: 10001
                                  }}
                                  ref={el => {
                                    if (el) {
                                      yearMenuRefs.current[optionKey] = el;
                                      const buttonRect = yearButtonRefs.current[optionKey].getBoundingClientRect();
                                      const viewportHeight = window.innerHeight;
                                      const minSpaceFromBottom = 30;
                                      const maxMenuHeight = 335;
                                      
                                      const spaceBelow = viewportHeight - buttonRect.bottom - 2;
                                      const spaceAbove = buttonRect.top - 2;
                                      
                                      let top, maxHeight;
                                      
                                      if (spaceBelow >= minSpaceFromBottom + 50) {
                                        top = buttonRect.bottom + 2;
                                        maxHeight = Math.min(maxMenuHeight, spaceBelow - minSpaceFromBottom);
                                      } else if (spaceAbove > spaceBelow && spaceAbove >= 50) {
                                        maxHeight = Math.min(maxMenuHeight, spaceAbove - minSpaceFromBottom);
                                        top = buttonRect.top - maxHeight - 2;
                                      } else {
                                        top = buttonRect.bottom + 2;
                                        maxHeight = Math.max(100, spaceBelow - minSpaceFromBottom);
                                      }
                                      
                                      el.style.top = `${top}px`;
                                      el.style.left = `${buttonRect.left}px`;
                                      el.style.minWidth = `${buttonRect.width}px`;
                                      el.style.maxHeight = `${maxHeight}px`;
                                    }
                                  }}
                                >
                                  {availableYearsForOption.map(year => (
                                    <div 
                                      key={year.value} 
                                      className={`year-dropdown-item ${selectedYear === year.value ? 'disabled' : ''}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (selectedYear !== year.value) {
                                          setSelectedYear(year.value);
                                          setOpenYearDropdown(null);
                                        }
                                      }}
                                    >
                                      {year.label}
                                    </div>
                                  ))}
                                </div>,
                                document.body
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="option-details">
                      <span className="match-type">{getMatchTypeDescription(option.matchType)}</span>
                      {option.jurisdictions && (
                        <span className="jurisdictions">
                          Includes: {option.jurisdictions}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        <div className="modal-actions">
          <button className="cancel-button" onClick={onClose}>
            Cancel
          </button>
          <button className="confirm-button" onClick={handleConfirm} disabled={!selectedOption}>
            View Results
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CityResolutionModal;